import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Building2, Users, AlertTriangle, Calendar, Megaphone, ClipboardList, Mail, Sliders, LogOut, Power, PowerOff, UserPlus, Inbox, Check, X, Copy, KeyRound } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { toast } from "sonner";

const HEALTH_BADGE_CLS = {
  active: "text-green-700 bg-green-100",
  slowing: "text-amber-700 bg-amber-100",
};
const LEAD_BADGE_CLS = {
  new: "bg-amber-100 text-amber-800",
  contacted: "bg-blue-100 text-blue-800",
  approved: "bg-green-100 text-green-800",
};

function TestEmailDialog() {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function send(e) {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/super-admin/test-email", { to });
      setResult(data);
      if (data.ok) toast.success(`Test email sent via ${data.provider}`);
      else toast.error(data.error || "Send failed");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setResult(null); setTo(""); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="sa-test-email-btn" className="rounded-full border-stone-300">
          <Mail size={16} className="mr-1.5" /> Test email
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="test-email-dialog">
        <DialogHeader><DialogTitle className="font-serif-display">Send test email</DialogTitle></DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <div>
            <Label>Recipient</Label>
            <Input type="email" required value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="you@example.com" data-testid="test-email-input" className="h-11 mt-1.5" />
          </div>
          <Button type="submit" disabled={busy} data-testid="test-email-send-btn"
            className="w-full bg-[#C05640] hover:bg-[#A8492F] rounded-full">
            {busy ? "Sending…" : "Send test email"}
          </Button>
        </form>
        {result && (
          <div className={`rounded-xl p-3 text-xs ${result.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`} data-testid="test-email-result">
            {result.ok
              ? <>Sent via <b>{result.provider}</b>{result.response?.data?.[0]?.message ? ` — ${result.response.data[0].message}` : ""}. Check inbox (and spam) at {to}.</>
              : <>Failed: {result.error || result.reason}</>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SendResetDialog() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function send(e) {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/super-admin/send-reset-by-email", { email });
      setResult(data);
      toast.success(`Reset link emailed to ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setResult(null); setEmail(""); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="sa-send-reset-btn" className="rounded-full border-stone-300">
          <KeyRound size={16} className="mr-1.5" /> Send password reset
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="send-reset-dialog">
        <DialogHeader><DialogTitle className="font-serif-display">Send password reset link</DialogTitle></DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <p className="text-xs text-stone-500">
            Enter the user&apos;s email. They&apos;ll receive a secure link to set a new password (link valid for 30 minutes).
          </p>
          <div>
            <Label>User email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="user@apartment.in" data-testid="send-reset-email" className="h-11 mt-1.5" />
          </div>
          <Button type="submit" disabled={busy} data-testid="send-reset-submit"
            className="w-full bg-[#C05640] hover:bg-[#A8492F] rounded-full">
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </form>
        {result?.ok && (
          <div className="rounded-xl p-3 text-xs bg-emerald-50 text-emerald-800" data-testid="send-reset-result">
            Reset link emailed to <b>{result.sent_to}</b>. Expires in {result.expires_in_minutes} min.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SuperAdminPage() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("communities");

  const load = useCallback(async () => {
    const [s, c, l] = await Promise.all([
      api.get("/super-admin/stats").then((r) => r.data),
      api.get("/super-admin/communities").then((r) => r.data),
      api.get("/super-admin/leads").then((r) => r.data).catch((e) => { console.error("leads load failed", e); return []; }),
    ]);
    setStats(s); setCommunities(c); setLeads(l);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggleSuspend(c) {
    try {
      const path = c.suspended ? "unsuspend" : "suspend";
      await api.post(`/super-admin/communities/${c.id}/${path}`);
      toast.success(c.suspended ? "Unsuspended" : "Suspended");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  const newLeads = leads.filter((l) => l.status === "new").length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="px-5 md:px-10 py-5 bg-white border-b border-stone-200 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Platform</div>
          <h1 className="font-serif-display text-2xl font-semibold mt-0.5">NivasOne Super-admin</h1>
        </div>
        <div className="flex items-center gap-2">
          <SendResetDialog />
          <TestEmailDialog />
          <OnboardCommunityDialog onDone={load} />
          <Button onClick={async () => { await logout(); nav("/login"); }} variant="ghost" data-testid="sa-logout"><LogOut size={18} className="mr-1.5" /> Sign out</Button>
        </div>
      </header>

      <main className="px-5 md:px-10 py-7 max-w-6xl">
        {stats && (
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sa-stats">
            <Tile icon={Building2} label="Communities" value={stats.communities} />
            <Tile icon={Users} label="Members" value={stats.users} />
            <Tile icon={AlertTriangle} label="Issues" value={stats.issues} />
            <Tile icon={Calendar} label="Meetings" value={stats.meetings} />
            <Tile icon={Megaphone} label="Notices" value={stats.notices} />
            <Tile icon={ClipboardList} label="To-dos" value={stats.actions} />
            <Tile icon={Mail} label="Emails logged" value={stats.emails_logged} />
            <Tile icon={Inbox} label="New leads" value={newLeads} />
          </section>
        )}

        <div className="mt-9 inline-flex border border-stone-200 rounded-full bg-white text-sm overflow-hidden" data-testid="sa-tabs">
          <button onClick={() => setTab("communities")}
            data-testid="sa-tab-communities"
            className={`px-4 py-1.5 ${tab === "communities" ? "bg-[#C05640] text-white" : "text-stone-600"}`}>
            Communities ({communities.length})
          </button>
          <button onClick={() => setTab("leads")}
            data-testid="sa-tab-leads"
            className={`px-4 py-1.5 ${tab === "leads" ? "bg-[#C05640] text-white" : "text-stone-600"}`}>
            Leads {newLeads > 0 && <span className="ml-1 text-[10px] bg-white/20 px-1.5 rounded">{newLeads} new</span>}
          </button>
        </div>

        {tab === "communities" && (
          <div className="space-y-3 mt-5">
            {communities.map((c) => (
              <div key={c.id} className={`bg-white border rounded-2xl p-5 ${c.suspended ? "border-red-200 bg-red-50" : "border-stone-200"}`} data-testid={`sa-comm-${c.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-serif-display text-xl font-medium">{c.name}</h3>
                      <span className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{c.plan || "pilot"}</span>
                      {c.suspended && <span className="text-[10px] uppercase tracking-wider font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">Suspended</span>}
                      {c.health?.health && (
                        <span data-testid={`sa-health-${c.id}`}
                          className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${HEALTH_BADGE_CLS[c.health.health] || "text-stone-600 bg-stone-100"}`}>{c.health.health}</span>
                      )}
                    </div>
                    <div className="text-sm text-stone-500 mt-0.5">{c.address} · since {fmtDate(c.created_at)}</div>
                    <div className="text-xs text-stone-600 mt-2 flex items-center gap-3 flex-wrap">
                      <span>{c.user_count} members</span>
                      <span>·</span>
                      <span>{c.flat_count} flats</span>
                      <span>·</span>
                      <span>{c.issue_count} issues</span>
                      <span>·</span>
                      <span>{c.meeting_count} meetings</span>
                      <span>·</span>
                      <span>{c.notice_count} notices</span>
                    </div>
                    {c.health && (
                      <div className="text-[11px] text-stone-500 mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`sa-activity-${c.id}`}>
                        <span>DAU {c.health.dau} · WAU {c.health.wau} · MAU {c.health.mau}</span>
                        <span>·</span>
                        <span>Adoption {c.health.adoption_pct}%</span>
                        <span>·</span>
                        <span>{c.health.events_7d} events / 7d</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <QuotaDialog community={c} onDone={load} />
                    <Button onClick={() => toggleSuspend(c)} variant="outline" size="sm" className="rounded-full" data-testid={`sa-toggle-${c.id}`}>
                      {c.suspended ? <><Power size={14} className="mr-1.5" />Unsuspend</> : <><PowerOff size={14} className="mr-1.5" />Suspend</>}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "leads" && (
          <LeadsList leads={leads} onChange={load} />
        )}
      </main>
    </div>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4">
      <Icon size={18} className="text-[#C05640]" />
      <div className="mt-2 font-serif-display text-2xl font-semibold">{value}</div>
      <div className="text-xs text-stone-500">{label}</div>
    </div>
  );
}

function QuotaDialog({ community, onDone }) {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(community.plan || "pilot");
  const [overrides, setOverrides] = useState(community.quota_overrides || {});

  async function save() {
    try {
      await api.patch(`/super-admin/communities/${community.id}/quotas`, { plan, overrides });
      toast.success("Quotas updated"); setOpen(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  const fields = [
    ["ai_transcribe_minutes", "AI transcription mins/month"],
    ["ai_calls", "AI calls/month"],
    ["wa_summaries", "WhatsApp summaries/month"],
    ["emails", "Emails/month"],
    ["storage_mb", "Storage MB"],
    ["flats", "Max flats"],
    ["members", "Max members"],
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full" data-testid={`sa-quotas-${community.id}`}><Sliders size={14} className="mr-1.5" />Plan & quotas</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif-display">{community.name} — plan & quotas</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Plan</Label>
            <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="sa-plan" className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white capitalize">
              {["pilot", "starter", "pro"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <p className="text-xs text-stone-500">Overrides leave blank to use plan defaults. Enter a number to override.</p>
          {fields.map(([key, label]) => (
            <div key={key} className="grid grid-cols-2 gap-3 items-center">
              <Label className="mt-0">{label}</Label>
              <Input type="number" value={overrides[key] ?? ""} onChange={(e) => setOverrides({ ...overrides, [key]: e.target.value === "" ? null : Number(e.target.value) })}
                data-testid={`sa-q-${key}`} className="h-10" placeholder="default" />
            </div>
          ))}
          <Button onClick={save} data-testid="sa-q-save" className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function OnboardCommunityDialog({ onDone, prefilledLead }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", address: "", city: "", plan: "pilot",
    champion_name: "", champion_email: "", champion_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (prefilledLead) {
      setForm({
        name: prefilledLead.apartment_name || "",
        address: "",
        city: prefilledLead.city || "",
        plan: "pilot",
        champion_name: prefilledLead.contact_name || "",
        champion_email: prefilledLead.contact_email || "",
        champion_phone: prefilledLead.contact_phone || "",
      });
      setOpen(true);
    }
  }, [prefilledLead]);

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/super-admin/onboard-community", {
        ...form,
        from_lead_id: prefilledLead?.id || null,
      });
      setCreated(r.data);
      toast.success(`${form.name} onboarded`);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  function close() { setOpen(false); setCreated(null); setForm({ name: "", address: "", city: "", plan: "pilot", champion_name: "", champion_email: "", champion_phone: "" }); }
  function waText() {
    if (!created) return "";
    const url = "https://nivasone.com";
    return `Hi ${form.champion_name.split(" ")[0]}, your NivasOne workspace for *${form.name}* is ready.\n\nLogin: ${url}/login\nEmail: ${created.champion.email}\nTemporary password: ${created.champion.temp_password}\n\nPlease change your password after first login.\n\n— NivasOne team`;
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else setOpen(true); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-[#C05640] hover:bg-[#A54734]" data-testid="sa-onboard-btn">
          <UserPlus size={14} className="mr-1.5" /> Onboard new community
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="sa-onboard-dialog">
        <DialogHeader><DialogTitle className="font-serif-display">{created ? "Community created" : "Onboard new community"}</DialogTitle></DialogHeader>
        {!created ? (
          <form onSubmit={submit} className="space-y-3 mt-2" data-testid="sa-onboard-form">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Apartment name</Label><Input value={form.name} required onChange={(e) => setForm({...form, name: e.target.value})} data-testid="onb-name" className="h-11 mt-1.5" placeholder="Sundar Apartments" /></div>
              <div>
                <Label>Plan</Label>
                <select value={form.plan} onChange={(e) => setForm({...form, plan: e.target.value})} data-testid="onb-plan" className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white capitalize">
                  {["pilot", "starter", "pro"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div><Label>Address</Label><Input value={form.address} required onChange={(e) => setForm({...form, address: e.target.value})} data-testid="onb-address" className="h-11 mt-1.5" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} data-testid="onb-city" className="h-11 mt-1.5" /></div>
            <div className="border-t border-stone-200 pt-3">
              <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Champion (committee admin)</div>
              <div><Label>Name</Label><Input value={form.champion_name} required onChange={(e) => setForm({...form, champion_name: e.target.value})} data-testid="onb-cname" className="h-11 mt-1.5" /></div>
              <div className="mt-3"><Label>Email</Label><Input type="email" value={form.champion_email} required onChange={(e) => setForm({...form, champion_email: e.target.value})} data-testid="onb-cemail" className="h-11 mt-1.5" /></div>
              <div className="mt-3"><Label>Phone (optional)</Label><Input value={form.champion_phone} onChange={(e) => setForm({...form, champion_phone: e.target.value})} data-testid="onb-cphone" className="h-11 mt-1.5" placeholder="+91-…" /></div>
            </div>
            <p className="text-[11px] text-stone-500">A welcome email with login details will be sent (or logged if email is in pilot mode). The champion can then add flats and invite residents through onboarding.</p>
            <Button type="submit" disabled={busy} data-testid="onb-save" className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">{busy ? "Creating…" : "Create community"}</Button>
          </form>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="bg-[#FDFBF7] border border-[#E8D8D3] rounded-xl p-4">
              <div className="text-xs text-stone-500">Login email</div>
              <div className="font-mono text-sm">{created.champion.email}</div>
              <div className="text-xs text-stone-500 mt-3">Temporary password</div>
              <div className="font-mono text-sm flex items-center gap-2">
                {created.champion.temp_password}
                <button onClick={() => { navigator.clipboard.writeText(created.champion.temp_password); toast.success("Copied"); }}
                  className="text-stone-400 hover:text-stone-700" data-testid="onb-copy"><Copy size={14} /></button>
              </div>
            </div>
            <Button asChild className="w-full h-11 rounded-full bg-[#25D366] hover:bg-[#1EBE55] text-white" data-testid="onb-wa">
              <a href={`https://wa.me/?text=${encodeURIComponent(waText())}`} target="_blank" rel="noreferrer">
                <FaWhatsapp className="mr-2" size={18} /> Send welcome on WhatsApp
              </a>
            </Button>
            <Button variant="outline" onClick={close} className="w-full rounded-full" data-testid="onb-done">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LeadsList({ leads, onChange }) {
  const [pickedLead, setPickedLead] = useState(null);

  async function setStatus(lead, status) {
    try {
      await api.patch(`/super-admin/leads/${lead.id}`, { status });
      toast.success(`Lead marked ${status}`);
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function del(lead) {
    if (!confirm("Delete this lead?")) return;
    try { await api.delete(`/super-admin/leads/${lead.id}`); toast.success("Deleted"); onChange?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  if (leads.length === 0) {
    return <div className="text-stone-500 italic py-12 text-center" data-testid="sa-leads-empty">No demo requests yet. The public form lives at /request-demo.</div>;
  }
  return (
    <div className="mt-5 space-y-3">
      {leads.map((l) => (
        <div key={l.id} className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`sa-lead-${l.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-serif-display text-xl font-medium">{l.apartment_name}</h3>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${LEAD_BADGE_CLS[l.status] || "bg-stone-100 text-stone-600"}`}>{l.status}</span>
              </div>
              <div className="text-sm text-stone-600 mt-1">
                {l.contact_name} · {l.role_in_apartment || "—"} · {l.city}{l.flat_count ? ` · ${l.flat_count} flats` : ""}
              </div>
              <div className="text-xs text-stone-500 mt-1 flex items-center gap-3 flex-wrap">
                <a href={`mailto:${l.contact_email}`} className="hover:text-[#C05640] inline-flex items-center gap-1"><Mail size={11} />{l.contact_email}</a>
                {l.contact_phone && <a href={`tel:${l.contact_phone}`} className="hover:text-[#C05640] inline-flex items-center gap-1"><FaWhatsapp size={11} className="text-[#25D366]" />{l.contact_phone}</a>}
                <span>· {fmtDate(l.created_at)}</span>
              </div>
              {l.message && <div className="mt-2 text-sm text-stone-700 italic bg-stone-50 p-2 rounded">{l.message}</div>}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {l.status === "new" && (
                <Button size="sm" variant="outline" onClick={() => setStatus(l, "contacted")} className="rounded-full" data-testid={`lead-contact-${l.id}`}>
                  <Check size={12} className="mr-1" />Mark contacted
                </Button>
              )}
              {l.status !== "approved" && (
                <Button size="sm" onClick={() => setPickedLead(l)} className="rounded-full bg-[#C05640] hover:bg-[#A54734]" data-testid={`lead-onboard-${l.id}`}>
                  <UserPlus size={12} className="mr-1" />Onboard
                </Button>
              )}
              {l.status === "new" && (
                <Button size="sm" variant="ghost" onClick={() => setStatus(l, "rejected")} className="rounded-full text-stone-500" data-testid={`lead-reject-${l.id}`}>
                  <X size={12} className="mr-1" />Reject
                </Button>
              )}
              <button onClick={() => del(l)} className="text-stone-400 hover:text-red-600 text-xs" data-testid={`lead-del-${l.id}`}>Delete</button>
            </div>
          </div>
        </div>
      ))}
      {pickedLead && (
        <OnboardCommunityDialog
          prefilledLead={pickedLead}
          onDone={() => { setPickedLead(null); onChange?.(); }}
        />
      )}
    </div>
  );
}
